import { useCallback, useId, type ReactNode } from 'react';
import styled from 'styled-components';
import { useSavePrcParamsMutation, type PrcVersion, type PrcVersionPayload } from '@shared/api';
import { Select } from '@shared/ui/Input';
import {
  REPAIR_ZONES,
  ScrollRegion,
  TEMPERATURE_ZONES,
  VisuallyHidden,
  blockNumber,
  fleetLeading,
  fleetType,
  typo,
} from '@entities/fleet';
import { buildParamsLayout } from '@entities/prc';
import { FieldHint, FieldLabel, Notice } from '@widgets/FleetRegistry/ui';
import type { PrcAccess } from '../access';
import {
  CompactTable,
  GridInput,
  NARROW,
  ReadValue,
  Section,
  SectionHead,
  SectionNote,
  SectionTitle,
} from '../ui';
import { EditorNotices, EditorSaveBar, ReadOnlyNote } from './EditorChrome';
import { useBlockEditor, type BlockEditor } from './useBlockEditor';

const pick = (v: PrcVersion) => v.params;
const layoutOf = (v: PrcVersion) => buildParamsLayout(v.params);

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(5)}`};

  @container prc (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container prc (max-width: ${NARROW}) {
    grid-template-columns: minmax(0, 1fr);
  }

  & input {
    text-align: right;
  }
`;

const Unit = styled.span`
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Flag = styled.label`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  align-items: start;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  cursor: pointer;

  & + & {
    margin-top: ${({ theme }) => theme.spacing(2)};
  }

  & > input {
    width: 18px;
    height: 18px;
    margin: 2px 0 0;
    accent-color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
  }

  & > input:disabled {
    cursor: default;
  }

  & > .title {
    font-size: ${fleetType.base};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
  }

  & > .text {
    grid-column: 2;
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  &[data-on='true'] {
    border-color: ${({ theme }) => theme.colors.status.warning}99;
  }
`;

const Overhead = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const FixNote = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Facts = styled.p`
  margin: 0;
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

function NumberField({
  editor,
  k,
  label,
  unit,
  hint,
  placeholder,
}: {
  editor: BlockEditor;
  k: string;
  label: string;
  unit: string;
  hint?: ReactNode;
  placeholder?: string;
}) {
  const b = editor.bind(k);
  const err = editor.error(k);
  return (
    <div>
      <FieldLabel htmlFor={b.id}>
        <span>{label}</span>
        <Unit>{unit}</Unit>
      </FieldLabel>
      {editor.editable ? (
        <GridInput
          {...b}
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          aria-describedby={
            [hint ? `${b.id}-hint` : null, err ? `${b.id}-error` : null]
              .filter(Boolean)
              .join(' ') || undefined
          }
        />
      ) : (
        <ReadValue>{editor.values[k] || placeholder || '—'}</ReadValue>
      )}
      {hint && <FieldHint id={`${b.id}-hint`}>{hint}</FieldHint>}
      {err && <VisuallyHidden id={`${b.id}-error`}>{err}</VisuallyHidden>}
    </div>
  );
}

function ZoneField({
  editor,
  k,
  label,
  options,
  hint,
}: {
  editor: BlockEditor;
  k: string;
  label: string;
  options: readonly { key: string; label: string }[];
  hint: string;
}) {
  const b = editor.bind(k);
  const value = editor.values[k] ?? '';
  const list =
    options.some((o) => o.key === value) || value === ''
      ? options
      : [...options, { key: value, label: value }];
  return (
    <div>
      <FieldLabel htmlFor={b.id}>
        <span>{label}</span>
      </FieldLabel>
      <Select
        id={b.id}
        value={value}
        disabled={!editor.editable}
        data-changed={b['data-changed']}
        aria-describedby={`${b.id}-hint`}
        onChange={(e) => editor.setValue(k, e.target.value)}
      >
        {value === '' && <option value="">Не задана</option>}
        {list.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </Select>
      <FieldHint id={`${b.id}-hint`}>{hint}</FieldHint>
    </div>
  );
}

function FlagField({
  editor,
  k,
  title,
  text,
}: {
  editor: BlockEditor;
  k: string;
  title: string;
  text: string;
}) {
  const on = editor.values[k] === 'true';
  return (
    <Flag data-on={on ? 'true' : undefined}>
      <input
        type="checkbox"
        checked={on}
        disabled={!editor.editable}
        onChange={(e) => editor.setValue(k, e.target.checked ? 'true' : 'false')}
      />
      <span className="title">{title}</span>
      <span className="text">{typo(text)}</span>
    </Flag>
  );
}

/**
 * Вкладка «Параметры»: зона режима и ремонта, общецеховые по годам,
 * рентабельность, НДС, доставка шин, общий коэффициент гидрожидкости и
 * исправления формул книги (по умолчанию выключены — «как в Excel»).
 */
export function ParamsTab({
  payload,
  access,
  onDirtyChange,
  onCompare,
}: {
  payload: PrcVersionPayload;
  access: PrcAccess;
  onDirtyChange: (count: number) => void;
  onCompare: () => void;
}) {
  const uid = useId();
  const [saveParams] = useSavePrcParamsMutation();
  const variantId = payload.variant?.id;
  const save = useCallback(
    (args: { id: string; block: PrcVersion['params']; expected_updated_at: string }) =>
      saveParams({ ...args, variant_id: variantId }).unwrap(),
    [saveParams, variantId],
  );
  const editor = useBlockEditor({
    payload,
    canWrite: access.canWrite,
    pick,
    layout: layoutOf,
    save,
    onDirtyChange,
    idPrefix: uid,
  });
  const l = editor.layout;
  const params = editor.base.params;
  const corrected =
    editor.values[l.hydraulicLiters] === 'true' || editor.values[l.mergeTires] === 'true';

  return (
    <>
      <ReadOnlyNote status={editor.base.status} canWrite={access.canWrite} />
      <EditorNotices editor={editor} lead="Параметры сохранены" onCompare={onCompare} />

      <Section aria-labelledby={`${uid}-horizon-h`}>
        <SectionHead>
          <SectionTitle id={`${uid}-horizon-h`}>
            <span className="n" aria-hidden="true">
              {blockNumber(0)}
            </span>
            Горизонт
          </SectionTitle>
        </SectionHead>
        <Facts>
          Базовый год <strong>{params.base_year}</strong>, годы расчёта{' '}
          <strong>{params.years.join(', ')}</strong>.{' '}
          {typo('Горизонт задаёт книга ЭММ, из которой создана версия; здесь он не меняется.')}
        </Facts>
      </Section>

      <Section aria-labelledby={`${uid}-mode-h`}>
        <SectionHead>
          <SectionTitle id={`${uid}-mode-h`}>
            <span className="n" aria-hidden="true">
              {blockNumber(1)}
            </span>
            Режим работы и ремонт
          </SectionTitle>
        </SectionHead>
        <Fields>
          <ZoneField
            editor={editor}
            k={l.zone}
            label="Температурная зона режима"
            options={TEMPERATURE_ZONES}
            hint={typo(
              'Годовой режим = базовый режим × коэффициент зоны из паспорта модели. В книге — V.',
            )}
          />
          <ZoneField
            editor={editor}
            k={l.repairZone}
            label="Зона нормы ремонта"
            options={REPAIR_ZONES.map((z) => ({ key: z.key, label: `${z.label} (${z.key})` }))}
            hint={typo('Какая итоговая норма затрат на ремонт и ТО из паспорта идёт в расчёт.')}
          />
        </Fields>
      </Section>

      <Section aria-labelledby={`${uid}-markup-h`}>
        <SectionHead>
          <SectionTitle id={`${uid}-markup-h`}>
            <span className="n" aria-hidden="true">
              {blockNumber(2)}
            </span>
            Наценки и налоги
          </SectionTitle>
        </SectionHead>
        <Fields>
          <NumberField
            editor={editor}
            k={l.profit}
            label="Рентабельность"
            unit="%"
            hint="Прибыль = себестоимость × рентабельность. В книге 20 %"
          />
          <NumberField editor={editor} k={l.vat} label="НДС" unit="%" hint="В книге 22 %" />
        </Fields>
        <Overhead>
          <SectionNote>
            {typo('Общецеховые — доля от суммы восьми статей маш.-часа, по годам, %:')}
          </SectionNote>
          <ScrollRegion label="Общецеховые по годам">
            <CompactTable>
              <thead>
                <tr>
                  {params.years.map((y) => (
                    <th key={y} scope="col" className="num">
                      {y}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {params.years.map((y) => {
                    const k = l.overhead[y];
                    if (!k) return <td key={y} />;
                    return (
                      <td key={y} className="num">
                        {editor.editable ? (
                          <GridInput
                            {...editor.bind(k)}
                            inputMode="decimal"
                            autoComplete="off"
                            spellCheck={false}
                            placeholder="0"
                            aria-label={`Общецеховые ${y}, %`}
                          />
                        ) : (
                          <ReadValue>{editor.values[k] || '0'}</ReadValue>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </CompactTable>
          </ScrollRegion>
        </Overhead>
      </Section>

      <Section aria-labelledby={`${uid}-tires-h`}>
        <SectionHead>
          <SectionTitle id={`${uid}-tires-h`}>
            <span className="n" aria-hidden="true">
              {blockNumber(3)}
            </span>
            Шины и гидрожидкость
          </SectionTitle>
        </SectionHead>
        <Fields>
          <NumberField
            editor={editor}
            k={l.tireCoef}
            label="Доставка и замена шин"
            unit="коэффициент"
            placeholder="1,35"
            hint="Комплект шин × коэффициент. Пусто — 1,35, как в книге"
          />
          <NumberField
            editor={editor}
            k={l.hydraulicOverride}
            label="Общая ячейка гидрожидкости"
            unit="ячейка DZ8/EA8"
            hint={typo(
              'Пусто — у каждой модели свои коэффициент доливок и периодичность замены. Заполнено — книга ставит это число в оба множителя, так считает Excel.',
            )}
          />
        </Fields>
      </Section>

      <Section aria-labelledby={`${uid}-fix-h`}>
        <SectionHead>
          <SectionTitle id={`${uid}-fix-h`}>
            <span className="n" aria-hidden="true">
              {blockNumber(4)}
            </span>
            Исправления формул книги
          </SectionTitle>
          <SectionNote>
            {typo('По умолчанию выключены: расчёт «как в Excel», до копейки с книгой.')}
          </SectionNote>
        </SectionHead>
        <FlagField
          editor={editor}
          k={l.hydraulicLiters}
          title="Исправить гидрожидкость"
          text="Книга умножает годовой расход в литрах на плотность (кг/л) и подписывает результат литрами. С исправлением — литры без плотности. Цены машин с гидравликой станут ниже, чем в Excel."
        />
        <FlagField
          editor={editor}
          k={l.mergeTires}
          title="Склеить типоразмеры шин"
          text="Excel ищет шину по точной строке: «225/75R16.» с точкой и «225/75R16» — разные цены. С исправлением — одна позиция, цена первой встретившейся."
        />
        {corrected && (
          <FixNote>
            <Notice $tone="warning" role="note">
              <span className="text">
                {typo(
                  'Включённое исправление расходится с Excel: после сохранения ПРЦ части моделей не совпадёт с книгой. Насколько — видно во вкладке «Было → стало».',
                )}
              </span>
            </Notice>
          </FixNote>
        )}
      </Section>

      <EditorSaveBar editor={editor} />
    </>
  );
}
